import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import React, { memo, useContext, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { Clock3, Flame } from "lucide-react";
import {
  LanguageNameSpaces,
  LineChartDataPointWithExchange,
  OrderTypes,
  PrecisionType,
  TradeData,
} from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { SocketPnlContext } from "@shared/features/dashboard/SocketPnl.context";
import { getAnnotationColor } from "@shared/features/trades/common/utils";
import { toFixed } from "@shared/utils/numbers";
import { hexToRGB } from "@shared/utils/misc";
import { getLiveTradeTimeDuration } from "@shared/utils/date";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { DISPLAY_CURRENCIES } from "common/constants";
import { Pill } from "common/NewLook";
import IconWithFallback from "Global/IconWithFallback";
import PriceChange from "common/Charts/PriceChange";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import Tooltip from "common/Tooltips/Tooltip";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { AccountObjectType } from "@shared/features/settings/types";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import useTradeCard from "features/dashboard/common/useTradeCard";
import { getUsdValueWithPrecision } from "features/dashboard/common/utils";
import NewLookSparkline, {
  SparklineLevel,
} from "features/dashboard/common/NewLookSparkline.tsx";
import { RawLineChartDataPoint } from "types";
import { TIME_RANGE_START } from "common/constants";
import { useTradePriceChart } from "@shared/features/trades/common/hooks";
import { useMobileTradePriceChart } from "@shared/features/dashboard/hooks";
import useLiveGraphData from "@shared/services/socket/useLiveGraphData";
import useMedia from "utils/hooks/useMedia";
import { DASHBOARD_TRADE_CARD_ID } from "testIds";
import { sharedLib } from "@shared/services/shared.service";

interface NewLookTradeCardProps {
  tradeData: TradeData;
  /** Price points for the sparkline, oldest first. */
  prices: number[];
  /** Raw graph data, used for the live "time in trade". */
  graphData?: LineChartDataPointWithExchange[] | RawLineChartDataPoint[];
  /** Narrower phone variant (horizontal card list). */
  compact?: boolean;
}

/**
 * The new look's open-trade card: symbol, side and time in trade, a quiet
 * price sparkline with the next orders, amount and PnL, and where the trade
 * comes from. Replaces the tall chart card on the dashboard.
 */
const NewLookTradeCard = ({
  tradeData,
  prices,
  graphData,
  compact,
}: NewLookTradeCardProps) => {
  const theme = useTheme();
  const isPhone = useMedia(["(max-width: 600px)"], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { openTradeExtraDetails, invalidAccountData, isTradeClosed } =
    useTradeCard({ tradeData, graphData });
  const { socketPnlValue } = useContext(SocketPnlContext) || {};
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const { getPrecision, getDisplayCurrencyAmountPrecision } = usePrecisions();

  const isHot =
    tradeData.closest_order?.is_hot && !invalidAccountData.isInvalidOrInactive;
  const isShort = tradeData["Long/Short"] === "Short";
  const leverage = [
    tradeData.margin_type,
    tradeData.leverage ? `${tradeData.leverage}x` : undefined,
  ]
    .filter(Boolean)
    .join(" ");
  const timeInTrade =
    getLiveTradeTimeDuration(tradeData.timestamp, graphData) ||
    tradeData.graph_data?.time_passed;

  const pnlData = socketPnlValue?.[tradeData.id] || tradeData;
  const pnlValue = isBtc
    ? pnlData.btc_amount_change
    : pnlData.usd_amount_change;
  const pnlPercent =
    pnlData?.graph_data && "profit" in pnlData.graph_data
      ? pnlData.graph_data.profit
      : 0;
  let pnlColor = theme.colors.titleColor;
  if (pnlValue > 0) pnlColor = theme.colors.green;
  else if (pnlValue < 0) pnlColor = theme.colors.red;

  const amount = isBtc
    ? tradeData.allocated_amount_btc
    : getUsdValueWithPrecision(tradeData.allocated_amount_usd);

  const levels = useMemo(() => {
    const lastPrice = prices[prices.length - 1];
    const result: SparklineLevel[] = [];
    const add = (
      key: string,
      price: number | undefined,
      orderType: OrderTypes,
      tooltip: string
    ) => {
      if (!price || !lastPrice) return;
      const distance = ((price - lastPrice) / lastPrice) * 100;
      const sign = distance > 0 ? "+" : distance < 0 ? "−" : "";
      result.push({
        key,
        value: price,
        color: getAnnotationColor(orderType, theme),
        label: `${sign}${toFixed(Math.abs(distance), 2)}%`,
        tooltip,
      });
    };
    const entryOrStop = tradeData.next_entry || tradeData.next_stop;
    add(
      "entry-or-stop",
      entryOrStop?.price,
      tradeData.next_entry ? OrderTypes.ENTRY : OrderTypes.STOP,
      tTrades("table.headers.next_entry_or_stop")
    );
    add(
      "take-profit",
      tradeData.next_take_profit?.price,
      OrderTypes.TAKE_PROFIT,
      tTrades("table.headers.next_take_profit")
    );
    return result;
  }, [prices, tradeData, theme, tTrades]);

  const symbol = (
    <InactiveAccountIcon
      accountId={tradeData.client_id}
      objectType={AccountObjectType.TRADE}
    >
      <SymbolName>{tradeData.symbol}</SymbolName>
    </InactiveAccountIcon>
  );

  return (
    <Card
      data-testid={DASHBOARD_TRADE_CARD_ID}
      $compact={compact}
      $isClosed={!!isTradeClosed}
      $clickable={!!openTradeExtraDetails}
      onClick={openTradeExtraDetails}
    >
      <Header>
        <IconWithSource
          symbol={tradeData.symbol}
          sourceType={tradeData.source_type}
          source={tradeData.group}
          mainIconSize={isPhone ? 24 : compact ? 30 : 32}
          style={ICON_STYLE}
        />
        <HeaderText>
          <SymbolRow>
            {symbol}
            {isHot && (
              <HotIcon
                size={13}
                strokeWidth={2}
                aria-label={tCommon("labels.hot_trade")}
              />
            )}
            {tradeData["Long/Short"] && (
              <SidePill tone={isShort ? "red" : "green"}>
                {tradeData["Long/Short"]}
              </SidePill>
            )}
          </SymbolRow>
          <SubRow>{leverage || tradeData.exchange}</SubRow>
        </HeaderText>
        {timeInTrade && (
          <Time>
            <Clock3 size={12} strokeWidth={2} />
            {timeInTrade}
          </Time>
        )}
      </Header>

      <SparkWrapper>
        <NewLookSparkline
          values={prices}
          levels={isPhone ? [] : levels}
          height={isPhone ? 30 : compact ? 44 : 52}
          gutter={compact ? 50 : 54}
        />
      </SparkWrapper>

      <Figures>
        <Figure>
          <FigureLabel>{t("open_trades.amount_label")}</FigureLabel>
          <FigureValue>
            <TextWithPrecisionAndIcon
              text={amount}
              precision={getPrecision(PrecisionType.BALANCE)}
              iconType={ICON_TYPES.DISPLAY_CURRENCY}
              textStyle={INHERIT_TEXT}
            />
          </FigureValue>
        </Figure>
        <Figure $end={true}>
          <FigureLabel>{t("open_trades.pnl_label")}</FigureLabel>
          <FigureValue style={{ color: pnlColor }}>
            <TextWithPrecisionAndIcon
              text={pnlValue}
              precision={getDisplayCurrencyAmountPrecision()}
              iconType={ICON_TYPES.DISPLAY_CURRENCY}
              textStyle={INHERIT_TEXT}
            />
            <PriceChange
              change={pnlPercent}
              size={11}
              useBackground={true}
              style={CHANGE_STYLE}
            />
          </FigureValue>
        </Figure>
      </Figures>

      <Footer>
        <Tooltip title={tradeData.account} placement="topLeft">
          <Account style={{ color: invalidAccountData.color }}>
            <IconWithFallback
              coinOrExchangeName={tradeData.exchange}
              iconType={ICON_TYPES.EXCHANGE}
              pxIconSize={14}
            />
            <AccountName>{tradeData.account}</AccountName>
          </Account>
        </Tooltip>
        {tradeData.group && (
          <Tooltip title={tradeData.group}>
            <Source>{tradeData.group}</Source>
          </Tooltip>
        )}
      </Footer>
    </Card>
  );
};

export default memo(NewLookTradeCard);

const ICON_STYLE = { marginRight: 14, flex: "none" };
const INHERIT_TEXT = { color: "inherit", fontSize: "inherit" };
const CHANGE_STYLE = { fontWeight: 500 };

const Card = styled.div<{
  $compact?: boolean;
  $isClosed: boolean;
  $clickable: boolean;
}>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px 16px 12px;
  background: ${(p) => p.theme.colors.cardBgColor};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${(p) => p.theme.radii.card}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 4px 10px;
    padding: 8px 10px;
    border-radius: 12px;
    box-shadow: ${(p) => p.theme.shadows.panel};
  }
  transition: border-color 150ms, box-shadow 150ms;
  ${(p) =>
    p.$clickable &&
    css`
      &:hover {
        ${p.theme.isDarkMode
          ? `border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.35)};`
          : `box-shadow: ${p.theme.shadows.hoverCard};`}
      }
    `}
  ${(p) =>
    p.$compact &&
    css`
      width: 272px;
      padding: 14px 14px 10px;
    `}
  ${(p) =>
    p.$isClosed &&
    css`
      border-color: ${p.theme.colors.red};
      animation: fadeout 3s ease-in-out forwards;
    `}
`;
const Header = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  min-width: 0;
`;
const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;
const SymbolRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;
const SymbolName = styled.span`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14.5px;
  line-height: 20px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const HotIcon = styled(Flame)`
  flex: none;
  color: ${(p) => p.theme.colors.red};
  fill: ${(p) => p.theme.colors.red};
  margin-left: -2px;
`;
const SidePill = styled(Pill)`
  flex: none;
  padding: 0 7px;
  font-size: 11px;
  line-height: 18px;
`;
const SubRow = styled.div`
  @media (max-width: 600px) {
    display: none;
  }
  margin-top: 1px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Time = styled.span`
  flex: none;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12px;
  line-height: 20px;
  font-variant-numeric: tabular-nums;
`;
const SparkWrapper = styled.div`
  margin: 14px 0 12px;
  @media (max-width: 600px) {
    margin: 0;
    align-self: center;
  }
`;
const Figures = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;
const Figure = styled.div<{ $end?: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$end ? "flex-end" : "flex-start")};
`;
const FigureLabel = styled.span`
  @media (max-width: 600px) {
    font-size: 10px;
    line-height: 13px;
  }
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12px;
  line-height: 16px;
`;
const FigureValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 16px;
  line-height: 22px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 13px;
    line-height: 18px;
    gap: 4px;
  }
`;
const Footer = styled.div`
  grid-column: 1 / -1;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: ${(p) => p.theme.borders.lightest};
  min-width: 0;
  @media (max-width: 600px) {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;
const Account = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
`;
const AccountName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Source = styled.span`
  flex: none;
  max-width: 50%;
  padding: 1px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border: ${(p) => p.theme.borders.lightest};
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 11.5px;
  line-height: 18px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface TradeCardWithDataProps {
  tradeData: TradeData;
}

/** Desktop card: prices since the trade opened, kept live. */
export const NewLookDashboardTradeCard = memo(
  ({ tradeData }: TradeCardWithDataProps) => {
    const { getPriceGraphDataStatus, args } = useTradePriceChart(
      TIME_RANGE_START,
      tradeData
    );
    const graphData =
      getPriceGraphDataStatus.data?.data.graph_data[TIME_RANGE_START];
    useLiveGraphData<LineChartDataPointWithExchange>(
      sharedLib.sharedApi.common,
      "getPriceGraphData",
      args,
      TIME_RANGE_START,
      tradeData.symbol,
      tradeData.exchange,
      true
    );
    const prices = useMemo(
      () => graphData?.map((point) => Number(point[2])) || [],
      [graphData]
    );
    return (
      <NewLookTradeCard
        tradeData={tradeData}
        prices={prices}
        graphData={graphData}
      />
    );
  }
);

/** Phone card: the last day of prices that comes with the trades list. */
export const NewLookMobileTradeCard = memo(
  ({ tradeData }: TradeCardWithDataProps) => {
    const { graphData } = useMobileTradePriceChart(tradeData);
    const prices = useMemo(
      () => graphData?.map((point) => Number(point[1])) || [],
      [graphData]
    );
    return (
      <NewLookTradeCard tradeData={tradeData} prices={prices} compact={true} />
    );
  }
);
